import React from "react";
import { ArrowRight, ChevronDown, ChevronUp, Trash2, MapPin } from "lucide-react";
import { DatePicker } from "@/components/ui/date-time-picker";
import CbpInfoCard from "./CbpInfoCard";
import MxCustomsCard from "./MxCustomsCard";
import EtaInfoCard from "./EtaInfoCard";
import FuelOrderSection from "./FuelOrderSection";
import PassengersSection from "./PassengersSection";
import CrewSection from "./CrewSection";

// Renders one leg card (header + collapsed/expanded body).
// All state lives in the orchestrator; this is purely presentational.
function LegSection({
  leg,
  legIndex,
  legsCount,
  prevLegDepartureDate,
  expandedLeg,
  setExpandedLeg,
  removeLeg,
  updateLeg,
  setLegs,
  setLegInherit,
  // top-level config from orchestrator
  userRole,
  isAppendMode,
  aircraftId,
  aogDateSet,
  baseAirport,
  baseForTz,
  userFuelUnit,
  setUserFuelUnit,
  // passengers
  currentOwnerPassengers,
  setPassengerCount,
  updatePassenger,
  removePassenger,
  selectSuggestedPassenger,
  // crew
  assignedCrew,
  crewLoadError,
  temporaryCrew,
  extCrewPickerLeg,
  setExtCrewPickerLeg,
  openExtCrewPicker,
  addExternalCrew,
  addExternalCrewFromTemp,
  updateExternalCrew,
  setExternalCrewFields,
  removeExternalCrew,
  toggleCrewMember,
  updateCrewFlightRole,
  isCrewSelected,
  // cbp modals openers
  onOpenCbpDetails,
  onOpenCbpPdf,
  // i18n
  t,
  i18n,
}) {
  const expanded = expandedLeg === legIndex;
  // Hay "tramo anterior" del cual copiar cuando no es el primer tramo, o cuando
  // es el tramo 0 en modo append (copia del último tramo del viaje padre).
  const canInherit = legIndex >= 1 || (isAppendMode && legIndex === 0);
  return (
    <div
      data-testid={`new-trip-leg-${legIndex}`}
      className="border border-slate-700 rounded-xl overflow-hidden"
    >
      {/* Leg Header */}
      <div
        className={`px-4 py-3 flex items-center justify-between cursor-pointer transition-colors ${
          expanded ? "bg-slate-800" : "bg-slate-800/50 hover:bg-slate-800"
        }`}
        onClick={() => setExpandedLeg(expanded ? -1 : legIndex)}
      >
        <div className="flex items-center gap-3">
          <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center">
            {legIndex + 1}
          </span>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-white font-medium">{leg.origin || "???"}</span>
            <ArrowRight className="w-4 h-4 text-slate-500" />
            <span className="text-white font-medium">{leg.destination || "???"}</span>
          </div>
          {leg.time_tbd && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400">
              {t("newTrip.timeTBD")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {legsCount > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeLeg(legIndex);
              }}
              className="p-1.5 hover:bg-red-500/20 rounded-lg text-slate-400 hover:text-red-400"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </div>

      {/* Leg Details (Expanded) */}
      {expanded && (
        <div className="p-4 space-y-4 bg-[#0f172a]/50">
          {/* Copiar del tramo anterior — banner DENTRO del tramo: decisión
              per-tramo (afecta solo a este) y primer elemento visible cuando el
              scroll salta al tramo recién agregado. */}
          {canInherit && (
            <div className="p-3 rounded-xl border border-slate-700 bg-[#0f172a]/60">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">
                {t("newTrip.inheritHeader")}
              </p>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={leg.inherit_pax !== false}
                    onChange={(e) => setLegInherit(legIndex, "pax", e.target.checked)}
                    className="w-4 h-4 accent-[#0052FF] cursor-pointer"
                  />
                  <span className="text-sm text-white">{t("newTrip.inheritPax")}</span>
                </label>
                {userRole !== "owner" && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={leg.inherit_crew !== false}
                      onChange={(e) => setLegInherit(legIndex, "crew", e.target.checked)}
                      className="w-4 h-4 accent-[#0052FF] cursor-pointer"
                    />
                    <span className="text-sm text-white">{t("newTrip.inheritCrew")}</span>
                  </label>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-2">{t("newTrip.inheritHint")}</p>
            </div>
          )}

          {/* Route */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                {t("newTrip.originRequired")}
              </label>
              <input
                type="text"
                value={leg.origin}
                onChange={(e) =>
                  updateLeg(
                    legIndex,
                    "origin",
                    userRole === "owner" ? e.target.value : e.target.value.toUpperCase()
                  )
                }
                placeholder={userRole === "owner" ? t("newTrip.airportExampleOwner") : "Ej: MMMX"}
                maxLength={userRole === "owner" ? 100 : 4}
                className="w-full bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
              />
              {/* Origin is a free field: prefilled with the previous leg's
                  destination as a convenience, but editable so an intermediate
                  or repositioning leg can start elsewhere. */}
              {userRole === "owner" && (
                <p className="text-[10px] text-slate-400 mt-1">{t("newTrip.airportHintOwner")}</p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                {t("newTrip.destinationRequired")}
              </label>
              <input
                type="text"
                value={leg.destination}
                onChange={(e) =>
                  updateLeg(
                    legIndex,
                    "destination",
                    userRole === "owner" ? e.target.value : e.target.value.toUpperCase()
                  )
                }
                placeholder={userRole === "owner" ? t("newTrip.airportExampleOwner") : "Ej: KJFK"}
                maxLength={userRole === "owner" ? 100 : 4}
                className="w-full bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
              />
              {userRole === "owner" && (
                <p className="text-[10px] text-slate-400 mt-1">{t("newTrip.airportHintOwner")}</p>
              )}
            </div>
          </div>

          {/* Technical customs detail (CBP office hours / PTL, MX customs
              windows) is operations/crew territory — owners don't read it.
              Hidden for the owner trip-request flow (fix: no technical info
              to owner). */}
          {userRole !== "owner" && (
            <>
              <CbpInfoCard
                leg={leg}
                legIndex={legIndex}
                baseForTz={baseForTz}
                t={t}
                onOpenDetails={onOpenCbpDetails}
                onOpenPdf={onOpenCbpPdf}
              />

              <MxCustomsCard leg={leg} t={t} />
            </>
          )}

          {/* Departure Date/Time */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-slate-400">{t("newTrip.departureRequired")}</label>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={leg.time_tbd}
                  onChange={(e) => updateLeg(legIndex, "time_tbd", e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-slate-600 bg-[#0f172a] text-orange-500"
                />
                <span className="text-orange-400">{t("newTrip.timeTBD")}</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <DatePicker
                value={leg.departure_date}
                onChange={(val) => updateLeg(legIndex, "departure_date", val)}
                placeholder={t("common.selectDate")}
                minDate={legIndex > 0 ? prevLegDepartureDate : undefined}
                redDates={aogDateSet}
              />
              {!leg.time_tbd ? (
                <input
                  type="text"
                  value={leg.departure_time}
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^0-9]/g, "");
                    if (val.length > 4) val = val.slice(0, 4);
                    if (val.length >= 3) val = val.slice(0, 2) + ":" + val.slice(2);
                    updateLeg(legIndex, "departure_time", val);
                  }}
                  placeholder="HH:MM"
                  className="w-full bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                />
              ) : (
                <div className="flex items-center justify-center bg-orange-500/10 border border-orange-500/20 rounded-lg px-3 py-2 text-sm text-orange-400">
                  {t("newTrip.timeTBD")}
                </div>
              )}
            </div>
          </div>

          {/* Estimated Flight Time & Arrival (Auto-calculated when aircraft
              selected). Technical (winds-aloft delta, tz offset) — hidden for
              the owner trip-request flow; ops computes the ETA. */}
          {userRole !== "owner" && (
            <>
              {aircraftId && leg.ete_formatted && !leg.time_tbd && <EtaInfoCard leg={leg} t={t} />}
              {!aircraftId &&
                leg.origin &&
                leg.destination &&
                leg.departure_date &&
                !leg.time_tbd && (
                  <div className="p-2 bg-slate-700/50 border border-slate-600 rounded-lg text-xs text-slate-400 text-center">
                    {t("newTrip.selectAircraftForEta")}
                  </div>
                )}
            </>
          )}

          {/* Options */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={leg.is_international}
                onChange={(e) => updateLeg(legIndex, "is_international", e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-[#0f172a] text-blue-500"
              />
              <span className="text-slate-300">{t("newTrip.international")}</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={leg.is_ferry_flight}
                onChange={(e) => updateLeg(legIndex, "is_ferry_flight", e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-[#0f172a] text-orange-500"
              />
              <span className="text-slate-300">{t("newTrip.ferryFlight")}</span>
            </label>
          </div>

          {/* Alternate Airport — a regulatory/dispatch concept (owners don't
              pick alternates). Hidden for the owner trip-request flow; it's
              optional, so ops fills it in later if needed. */}
          {userRole !== "owner" && (
            <div>
              <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {t("newTrip.alternateAirport")}{" "}
                <span className="text-slate-400 ml-1">{t("common.optional")}</span>
              </label>
              <input
                type="text"
                value={leg.alternate_airport || ""}
                onChange={(e) =>
                  updateLeg(legIndex, "alternate_airport", e.target.value.toUpperCase())
                }
                placeholder={t("newTrip.icaoExample")}
                maxLength={4}
                className="w-full bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF] placeholder:text-slate-400"
              />
            </div>
          )}

          {/* Fuel Order (pilot only, origin = base airport) */}
          {userRole === "pilot" &&
            baseAirport &&
            leg.origin &&
            leg.origin.toUpperCase() === baseAirport.toUpperCase() && (
              <FuelOrderSection
                leg={leg}
                legIndex={legIndex}
                userFuelUnit={userFuelUnit}
                setUserFuelUnit={setUserFuelUnit}
                updateLeg={updateLeg}
                t={t}
              />
            )}

          <PassengersSection
            leg={leg}
            legIndex={legIndex}
            currentOwnerPassengers={currentOwnerPassengers}
            setLegs={setLegs}
            setPassengerCount={setPassengerCount}
            updateLeg={updateLeg}
            updatePassenger={updatePassenger}
            removePassenger={removePassenger}
            selectSuggestedPassenger={selectSuggestedPassenger}
            t={t}
            i18n={i18n}
          />

          {/* Crew Assignment — hidden for owners */}
          {userRole !== "owner" && (
            <CrewSection
              leg={leg}
              legIndex={legIndex}
              assignedCrew={assignedCrew}
              crewLoadError={crewLoadError}
              temporaryCrew={temporaryCrew}
              extCrewPickerLeg={extCrewPickerLeg}
              setExtCrewPickerLeg={setExtCrewPickerLeg}
              openExtCrewPicker={openExtCrewPicker}
              addExternalCrew={addExternalCrew}
              addExternalCrewFromTemp={addExternalCrewFromTemp}
              updateExternalCrew={updateExternalCrew}
              setExternalCrewFields={setExternalCrewFields}
              removeExternalCrew={removeExternalCrew}
              toggleCrewMember={toggleCrewMember}
              updateCrewFlightRole={updateCrewFlightRole}
              isCrewSelected={isCrewSelected}
              t={t}
            />
          )}

          {/* Notes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {!leg.is_ferry_flight && (
              <div>
                <label className="text-xs text-slate-400 mb-1 block">
                  {t("newTrip.cateringNotesLabel")}
                </label>
                <textarea
                  value={leg.catering_notes}
                  onChange={(e) => updateLeg(legIndex, "catering_notes", e.target.value)}
                  placeholder={t("newTrip.dietaryHint")}
                  rows={2}
                  className="w-full bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF] resize-none"
                />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-400 mb-1 block">
                {t("newTrip.groundSupportLabel")}
              </label>
              <textarea
                value={leg.ground_service_notes}
                onChange={(e) => updateLeg(legIndex, "ground_service_notes", e.target.value)}
                placeholder={t("newTrip.groundSupportHint")}
                rows={2}
                className="w-full bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF] resize-none"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(LegSection);
